/**
 * Button Component — Premium button system
 * Variants: primary, secondary, ghost, gold, outline, premium, danger
 * Sizes: sm, md, lg, xl
 */
import React from 'react';
import { Loader2 } from 'lucide-react';

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  fullWidth = false,
  className = '',
  children,
  ...props
}) {
  const variants = {
    primary: 'bg-coral-500 hover:bg-coral-600 text-white shadow-soft hover:shadow-spring',
    secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-800',
    ghost: 'bg-transparent hover:bg-slate-100 text-slate-700',
    gold: 'bg-gold-500 hover:bg-gold-600 text-white shadow-md hover:shadow-lg',
    outline: 'bg-transparent border-2 border-coral-500 text-coral-500 hover:bg-coral-50',
    premium: 'bg-gradient-to-r from-gold-500 to-gold-400 text-white shadow-soft hover:shadow-spring',
    danger: 'bg-red-500 hover:bg-red-600 text-white',
    subtle: 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200',
  };

  const sizes = {
    sm: 'px-4 py-2 text-[13px] rounded-xl gap-1.5',
    md: 'px-6 py-3 text-[14px] rounded-2xl gap-2',
    lg: 'px-8 py-3.5 text-[15px] rounded-3xl gap-2.5',
    xl: 'px-10 py-4 text-[16px] rounded-full gap-3',
  };

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18,
    xl: 20,
  };

  return (
    <button
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center font-bold tracking-tight
        transition-all duration-400 ease-spring
        disabled:opacity-50 disabled:cursor-not-allowed
        active:scale-[0.95]
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {loading && <Loader2 size={iconSizes[size]} className="animate-spin" />}
      {!loading && icon && iconPosition === 'left' && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      {children}
      {!loading && icon && iconPosition === 'right' && (
        <span className="flex-shrink-0">{icon}</span>
      )}
    </button>
  );
}

/**
 * IconButton — Button with only an icon
 */
export function IconButton({
  variant = 'ghost',
  size = 'md',
  disabled = false,
  className = '',
  children,
  ...props
}) {
  const variants = {
    ghost: 'bg-transparent hover:bg-slate-100 text-slate-700 hover:text-slate-900',
    primary: 'bg-coral-500 hover:bg-coral-600 text-white shadow-soft hover:shadow-spring',
    secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700',
    outline: 'bg-transparent border-2 border-slate-200 hover:border-slate-300 text-slate-700',
  };

  const sizes = {
    sm: 'w-10 h-10 rounded-xl',
    md: 'w-12 h-12 rounded-2xl',
    lg: 'w-14 h-14 rounded-3xl',
    xl: 'w-16 h-16 rounded-full',
  };

  return (
    <button
      disabled={disabled}
      className={`
        inline-flex items-center justify-center
        transition-all duration-400 ease-spring
        disabled:opacity-50 disabled:cursor-not-allowed
        active:scale-[0.90]
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * ButtonGroup — Group of buttons
 */
export function ButtonGroup({ className = '', children }) {
  return (
    <div className={`inline-flex ${className}`}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        const isFirst = index === 0;
        const isLast = index === React.Children.count(children) - 1;
        return React.cloneElement(child, {
          className: `${child.props.className || ''} ${isFirst ? 'rounded-r-none' : ''
            } ${isLast ? 'rounded-l-none' : ''} ${!isFirst && !isLast ? 'rounded-none' : ''
            } ${!isLast ? 'border-r-0' : ''}`,
        });
      })}
    </div>
  );
}

export default Button;
